/**
 * Leave — apply, and track your own applications.
 * Route: /leave
 * @file src/web/features/leave/pages/index.tsx
 *
 * @llm-rule WHEN: Anyone at any rank needs time off
 * @llm-rule NOTE: Applying is open to every rank, including a Head or the Company Admin.
 *   Deciding is not — that lives on the oversight console for CTO/CEO only.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Skeleton,
  Textarea,
  toast,
  useConfirm,
} from '@bloomneo/uikit';
import { CalendarDays, Inbox, Plane } from 'lucide-react';
import { AuthGuard, useAuth } from '../../auth';
import { AppShell } from '../../../shared/components/AppShell';
import { Panel, SectionLabel } from '../../../shared/components/presence';
import { useAppRequest } from '../../../shared/hooks/useAppApi';
import {
  DEFAULT_CAPABILITIES,
  ROLE_LABEL,
  canApplyForLeave,
  isSupervisor,
  type CapabilityMap,
  type OrgRole,
} from '../../../shared/domain';
import { PersonAvatar } from '../../../shared/components/presence';
import {
  DAY_PART_LABEL,
  LEAVE_STATUS_LABEL,
  LEAVE_STATUS_TONE,
  LEAVE_TYPE_LABEL,
  describeSpan,
  todayISO,
  type LeaveRequest,
} from '../leave.types';
import { TeamLeave } from '../components/TeamLeave';
import { LeaveBalance, type Balance } from '../components/LeaveBalance';
import { ApprovalChain } from '../components/ApprovalChain';

function LeavePage() {
  const { user } = useAuth();
  const { request } = useAppRequest();
  const confirm = useConfirm();

  /** The approved leave being called off, and the reason typed for it. */
  const [callingOff, setCallingOff] = useState<LeaveRequest | null>(null);
  const [callOffReason, setCallOffReason] = useState('');
  const [callOffBusy, setCallOffBusy] = useState(false);

  const role = (user?.orgRole ?? 'EMPLOYEE') as OrgRole;
  /** The company's delegation map — decides whether a Head is on the clock,
   *  and therefore whether leave applies to them at all. */
  const [capabilities, setCapabilities] = useState<CapabilityMap>(DEFAULT_CAPABILITIES);
  const mayApply = canApplyForLeave(role, capabilities);

  const [pending, setPending] = useState<LeaveRequest[]>([]);
  const [canDecide, setCanDecide] = useState(false);
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  /** Everyone in my reporting line — who is off, and who signed it off. */
  const [teamLeave, setTeamLeave] = useState<LeaveRequest[]>([]);
  const [balances, setBalances] = useState<Balance[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  /** Which of my requests has its approval chain open. */
  const [openChain, setOpenChain] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    type: 'CASUAL',
    startDate: todayISO(),
    endDate: todayISO(),
    dayPart: 'FULL_DAY',
    reason: '',
  });

  const load = useCallback(async () => {
    const [mineResult, pendingResult, scopeResult, balanceResult] = await Promise.all([
      request<{ requests: LeaveRequest[] }>('/api/leave/mine'),
      request<{ pending: LeaveRequest[]; canDecide: boolean }>('/api/leave/pending'),
      // Everyone below me. A employee's scope is themselves, so this is
      // harmless for them and the section stays hidden.
      isSupervisor(role)
        ? request<{ requests: LeaveRequest[] }>('/api/leave/scope')
        : Promise.resolve({ ok: true as const, data: { requests: [] as LeaveRequest[] } }),
      request<{ balances: Balance[]; pendingRequests: number; year: number }>('/api/leave/balance'),
    ]);

    if (balanceResult.ok) {
      setBalances(balanceResult.data.balances);
      setPendingCount(balanceResult.data.pendingRequests);
    }

    if (mineResult.ok) setRequests(mineResult.data.requests);
    else toast.error(mineResult.error);

    if (pendingResult.ok) {
      setPending(pendingResult.data.pending);
      setCanDecide(pendingResult.data.canDecide);
    }

    if (scopeResult.ok) {
      // Drop my own rows — they already have their own section below.
      setTeamLeave(scopeResult.data.requests.filter((r) => r.user?.id !== user?.id));
    }

    setLoading(false);
  }, [request, role, user?.id]);

  /**
   * Decide a request to call off leave that was already approved.
   *
   * Approving hands the working day back — only then can they clock in again.
   * Rejecting leaves the approved leave exactly as it was.
   */
  async function decideCancellation(id: string, decision: 'APPROVE' | 'REJECT') {
    const result = await request<{ message: string }>(`/api/leave/${id}/cancellation`, {
      method: 'POST',
      body: { decision },
    });
    if (!result.ok) return toast.error(result.error);
    toast.success(result.data.message);
    void load();
  }

  /** One click — accept or reject. */
  async function decide(id: string, decision: 'APPROVE' | 'REJECT') {
    const result = await request(`/api/leave/${id}/decision`, {
      method: 'POST',
      body: { decision },
    });
    if (!result.ok) return toast.error(result.error);
    toast.success(decision === 'APPROVE' ? 'Leave approved' : 'Leave rejected');
    void load();
  }

  useEffect(() => {
    void request<{ capabilities: CapabilityMap }>('/api/org/capabilities').then((r) => {
      if (r.ok) setCapabilities(r.data.capabilities);
    });
  }, [request]);

  useEffect(() => {
    void load();
  }, [load]);

  const singleDay = form.startDate === form.endDate;

  // A half day is only meaningful on one date, so the option disappears the
  // moment the range widens — and any half-day already picked is reset.
  useEffect(() => {
    if (!singleDay && form.dayPart !== 'FULL_DAY') {
      setForm((f) => ({ ...f, dayPart: 'FULL_DAY' }));
    }
  }, [singleDay, form.dayPart]);

  const days = useMemo(() => {
    const start = new Date(form.startDate);
    const end = new Date(form.endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return 0;
    const span = Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1;
    if (span === 1 && form.dayPart !== 'FULL_DAY') return 0.5;
    return span;
  }, [form.startDate, form.endDate, form.dayPart]);

  /** What would be left if this request were approved — null for unquota'd types. */
  const selectedBalance = balances.find((b) => b.type === form.type);
  const remainingAfter = selectedBalance ? Math.max(0, selectedBalance.remaining - days) : null;
  const overQuota = Boolean(selectedBalance && days > selectedBalance.remaining);


  async function submit() {
    if (!form.reason.trim()) return toast.error('A reason is required');
    if (days <= 0) return toast.error('The end date cannot be before the start date');

    setBusy(true);
    try {
      const result = await request('/api/leave', { method: 'POST', body: form });
      if (!result.ok) return toast.error(result.error);

      toast.success('Leave application submitted');
      setForm((f) => ({ ...f, reason: '' }));
      void load();
    } finally {
      setBusy(false);
    }
  }

  async function withdraw(row: LeaveRequest) {
    const ok = await confirm({
      title: 'Withdraw this application?',
      description: `${describeSpan(row)} — it will no longer be waiting on anyone.`,
      confirmLabel: 'Withdraw',
      tone: 'destructive',
    });
    if (!ok) return;

    const result = await request(`/api/leave/${row.id}/cancel`, { method: 'POST' });
    if (!result.ok) return toast.error(result.error);
    toast.success('Application withdrawn');
    void load();
  }

  /**
   * Ask for APPROVED leave to be called off.
   *
   * Not a withdrawal: the company has already planned around the absence, so
   * this only asks, and the leave stands — including the block on clocking in —
   * until a Head decides. The reason is required because that is the only thing
   * they have to decide on.
   */
  async function askToCallOff() {
    if (!callingOff || !callOffReason.trim()) return;

    setCallOffBusy(true);
    try {
      const result = await request<{ message: string }>(
        `/api/leave/${callingOff.id}/cancel`,
        { method: 'POST', body: { reason: callOffReason.trim() } },
      );
      if (!result.ok) return toast.error(result.error);

      toast.success(result.data.message);
      setCallingOff(null);
      setCallOffReason('');
      void load();
    } finally {
      setCallOffBusy(false);
    }
  }

  return (
    <AppShell
      wide
      title="Leave"
      subtitle={
        mayApply
          ? 'Apply for time off and track where it stands.'
          : 'Approve or reject the time off your people ask for.'
      }
    >
      {/* What you have left, before anything else. */}
      {mayApply && balances.length > 0 && (
        <div className="mb-5">
          <LeaveBalance
            balances={balances}
            pendingRequests={pendingCount}
            year={new Date().getFullYear()}
          />
        </div>
      )}

      {/* Approvals first — this is the thing waiting on you */}
      {canDecide && (
        <Panel padded={false} className="mb-5 overflow-hidden">
          <div className="flex items-center justify-between border-b border-rule bg-paper-warm px-5 py-2.5">
            <div className="flex items-center gap-2">
              <Inbox className="size-3.5 text-ink-faint" />
              <SectionLabel>Waiting on you</SectionLabel>
            </div>
            {pending.length > 0 && (
              <span className="rounded-full bg-[var(--state-warn-bg)] px-2 py-0.5 text-[11px] font-semibold text-offsite">
                {pending.length}
              </span>
            )}
          </div>

          {pending.length === 0 ? (
            <p className="px-5 py-6 text-center text-[12.5px] text-ink-faint">
              No leave applications need your decision.
            </p>
          ) : (
            <ul className="divide-y divide-rule">
              {pending.map((row) => (
                <li key={row.id} className="flex flex-wrap items-start gap-3 px-5 py-3.5">
                  {row.user && <PersonAvatar person={row.user} size={30} />}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-2 text-[12.5px] text-ink">
                      <b className="font-semibold">{row.user?.name}</b>
                      <span className="text-ink-faint">
                        {row.user?.title || (row.user ? ROLE_LABEL[row.user.orgRole] : '')}
                      </span>
                      <span className="text-ink-ghost">·</span>
                      <span className="font-medium">{describeSpan(row)}</span>
                      <span className="rounded-full bg-paper-sunk px-2 py-0.5 text-[10.5px] text-ink-faint">
                        {LEAVE_TYPE_LABEL[row.type]}
                      </span>
                    </div>
                    <p className="mt-1 text-[12.5px] leading-relaxed text-ink-soft">“{row.reason}”</p>

                    {/* This row is not a new application — it is an ask to undo
                        one you already approved. Said plainly, because the two
                        decisions look identical otherwise. */}
                    {row.status === 'CANCEL_REQUESTED' && (
                      <p
                        className="mt-1.5 rounded-lg px-2.5 py-1.5 text-[11.5px] leading-relaxed"
                        style={{ background: 'var(--state-warn-bg)', color: 'var(--dot-offsite)' }}
                      >
                        <b className="font-semibold">Wants this leave called off</b>
                        {row.cancelReason ? ` — “${row.cancelReason}”` : ''}. They cannot clock in
                        until you approve it.
                      </p>
                    )}
                  </div>

                  <div className="flex flex-none gap-1.5">
                    <Button
                      size="sm"
                      className="h-7 rounded-full bg-appreciation px-3.5 text-[11.5px] text-white hover:bg-appreciation/90"
                      onClick={() =>
                        row.status === 'CANCEL_REQUESTED'
                          ? void decideCancellation(row.id, 'APPROVE')
                          : void decide(row.id, 'APPROVE')
                      }
                    >
                      {row.status === 'CANCEL_REQUESTED' ? 'Call it off' : 'Accept'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 rounded-full border-[var(--state-bad-border)] px-3.5 text-[11.5px] text-flag"
                      onClick={() =>
                        row.status === 'CANCEL_REQUESTED'
                          ? void decideCancellation(row.id, 'REJECT')
                          : void decide(row.id, 'REJECT')
                      }
                    >
                      {row.status === 'CANCEL_REQUESTED' ? 'Keep leave' : 'Reject'}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      )}

      <div className={`grid gap-5 ${mayApply ? 'lg:grid-cols-[380px_1fr]' : ''}`}>
        {/* ---- Apply. A Company Admin has nobody above them to approve it. ---- */}
        {mayApply && (
        <Panel>
          <h2 className="mb-4 flex items-center gap-2 text-[15px] font-semibold text-ink">
            <Plane className="size-4" />
            New application
          </h2>

          <div className="flex flex-col gap-4">
            {/* Chips, not a dropdown: four options are worth seeing at once,
                and each can carry how many days are left on it. */}
            <div>
              <div className="label-mono mb-2 text-ink-faint">Type</div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(LEAVE_TYPE_LABEL).map(([value, label]) => {
                  const on = form.type === value;
                  const left = balances.find((b) => b.type === value);
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setForm({ ...form, type: value })}
                      aria-pressed={on}
                      className="rounded-full px-3.5 py-1.5 text-[13px] transition-colors"
                      style={{
                        border: on ? '2px solid var(--ink)' : '1.5px solid var(--rule)',
                        background: on ? 'var(--ink)' : 'transparent',
                        color: on ? 'var(--paper)' : 'var(--ink-soft)',
                      }}
                    >
                      {label.replace(' leave', '')}
                      {left && (
                        <span className={`font-clock ml-1.5 text-[11px] ${on ? 'opacity-70' : 'opacity-60'}`}>
                          {left.remaining}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <FormField label="From" required>
                <Input
                  type="date"
                  value={form.startDate}
                  onChange={(e) => {
                    const startDate = e.target.value;
                    // Keep the range valid as you pick — an end before the
                    // start is never what anyone means.
                    setForm((f) => ({
                      ...f,
                      startDate,
                      endDate: f.endDate < startDate ? startDate : f.endDate,
                    }));
                  }}
                />
              </FormField>

              <FormField label="To" required>
                <Input
                  type="date"
                  min={form.startDate}
                  value={form.endDate}
                  onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                />
              </FormField>
            </div>

            <div>
              <div className="label-mono mb-2 text-ink-faint">Duration</div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(DAY_PART_LABEL).map(([value, label]) => {
                  const on = form.dayPart === value;
                  // A half day is only meaningful on one date.
                  const blocked = !singleDay && value !== 'FULL_DAY';
                  return (
                    <button
                      key={value}
                      type="button"
                      disabled={blocked}
                      onClick={() => setForm({ ...form, dayPart: value })}
                      aria-pressed={on}
                      className="rounded-full px-3.5 py-1.5 text-[13px] transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                      style={{
                        border: on ? '2px solid var(--ink)' : '1.5px solid var(--rule)',
                        background: on ? 'var(--ink)' : 'transparent',
                        color: on ? 'var(--paper)' : 'var(--ink-soft)',
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              {!singleDay && (
                <p className="mt-1.5 text-[11px] text-ink-ghost">
                  Half days apply to a single date only.
                </p>
              )}
            </div>

            <FormField label="Reason" required>
              <Textarea
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                rows={3}
                placeholder="Family wedding out of town."
                className="resize-none"
              />
            </FormField>

            {/* GOES TO — who decides, and who else will see it. */}
            <div
              className="rounded-[11px] px-3.5 py-3"
              style={{
                border: '2px solid var(--rule)',
                background:
                  'repeating-linear-gradient(135deg, var(--paper-sunk) 0 6px, var(--paper) 6px 12px)',
              }}
            >
              <div className="label-mono text-ink-faint">Goes to</div>

              <div className="mt-1.5 flex items-center gap-2 text-[13.5px] text-ink">
                <span className="size-2 flex-none rounded-full" style={{ background: 'var(--brand)' }} />
                Your Head or the Company Admin decides
              </div>
              <div className="mt-1 flex items-center gap-2 text-[12.5px] text-ink-faint">
                <span
                  className="size-2 flex-none rounded-full"
                  style={{ border: '1.5px solid var(--ink-ghost)' }}
                />
                Then visible to everyone above you
              </div>

              <div className="mt-2.5 border-t border-rule pt-2 text-[12.5px] text-ink">
                {days > 0 ? (
                  <>
                    <b className="font-clock font-bold">{days}</b>{' '}
                    {days === 1 ? 'day' : 'days'}
                    {remainingAfter !== null && (
                      <span className="text-ink-faint">
                        {' '}· {remainingAfter} left after this
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-ink-faint">Pick a valid date range</span>
                )}
              </div>
            </div>

            <Button
              disabled={busy || days <= 0 || !form.reason.trim() || overQuota}
              onClick={() => void submit()}
              className="btn-brand h-12 w-full"
            >
              {busy ? 'Submitting…' : overQuota ? 'Not enough leave left' : 'Submit request'}
            </Button>
          </div>
        </Panel>
        )}

        {/* ---- Who is off, and who signed it off ---- */}
        {isSupervisor(role) && <TeamLeave rows={teamLeave} />}

        {/* ---- My applications ---- */}
        <Panel padded={false} className="overflow-hidden">
          <div className="border-b border-rule px-5 py-3">
            <SectionLabel>My applications</SectionLabel>
          </div>

          {loading ? (
            <div className="p-5">
              <Skeleton className="h-40 w-full rounded-xl" />
            </div>
          ) : requests.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-5 py-12 text-center">
              <CalendarDays className="size-8 text-ink-ghost" strokeWidth={1.25} />
              <p className="text-[13px] text-ink-faint">
                You haven’t applied for any leave yet.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-rule">
              {requests.map((row) => {
                const tone = LEAVE_STATUS_TONE[row.status];
                return (
                  <li key={row.id} className="flex flex-wrap items-start gap-3 px-5 py-3.5">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[13.5px] font-medium text-ink">
                          {describeSpan(row)}
                        </span>
                        <span
                          className="label-mono rounded-md px-2 py-0.5 text-[10px]"
                          style={{ background: tone.bg, color: tone.fg }}
                        >
                          {LEAVE_STATUS_LABEL[row.status]}
                        </span>
                        <span className="text-[11.5px] text-ink-ghost">
                          {LEAVE_TYPE_LABEL[row.type]}
                        </span>
                      </div>

                      <p className="mt-1 text-[12.5px] leading-relaxed text-ink-soft">{row.reason}</p>

                      {row.decidedBy && row.status !== 'CANCEL_REQUESTED' && (
                        <p className="mt-1 text-[11.5px] text-ink-faint">
                          {row.status === 'APPROVED' ? 'Approved' : 'Rejected'} by{' '}
                          {row.decidedBy.name}
                          {row.decisionNote ? ` — “${row.decisionNote}”` : ''}
                        </p>
                      )}

                      {row.status === 'CANCEL_REQUESTED' && (
                        <p
                          className="mt-1.5 rounded-lg px-2.5 py-1.5 text-[11.5px] leading-relaxed"
                          style={{ background: 'var(--state-warn-bg)', color: 'var(--dot-offsite)' }}
                        >
                          You asked for this to be called off
                          {row.cancelReason ? ` — “${row.cancelReason}”` : ''}. Your leave stands
                          until a Head approves it, so you still cannot clock in.
                        </p>
                      )}
                    </div>

                    <div className="flex flex-none items-center gap-1.5">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 rounded-full px-3 text-[11.5px]"
                        onClick={() => setOpenChain(openChain === row.id ? null : row.id)}
                        aria-expanded={openChain === row.id}
                      >
                        {openChain === row.id ? 'Hide' : 'Track'}
                      </Button>

                      {row.status === 'PENDING' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 rounded-full px-3 text-[11.5px] text-flag"
                          onClick={() => void withdraw(row)}
                        >
                          Withdraw
                        </Button>
                      )}

                      {/* Approved leave is not yours to undo on your own. */}
                      {row.status === 'APPROVED' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 rounded-full px-3 text-[11.5px]"
                          onClick={() => {
                            setCallingOff(row);
                            setCallOffReason('');
                          }}
                        >
                          Ask to call off
                        </Button>
                      )}
                    </div>

                    {/* Where it has got to, and who is sitting on it. */}
                    {openChain === row.id && (
                      <div className="w-full border-t border-dashed border-rule pt-3.5">
                        <ApprovalChain row={row} approverLabel="Your manager, Head or the Company Admin" />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>
      </div>
      {/* Asking for approved leave to be called off. */}
      <Dialog
        open={callingOff !== null}
        onOpenChange={(open) => {
          if (!open) setCallingOff(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ask for this leave to be called off</DialogTitle>
            <DialogDescription>
              {callingOff ? describeSpan(callingOff) : ''} — this was already approved, so a Head
              has to agree before the day comes back. Until they do, your leave stands and you
              cannot clock in.
            </DialogDescription>
          </DialogHeader>

          <FormField label="Why do you want to work these days after all?" required>
            <Textarea
              value={callOffReason}
              onChange={(e) => setCallOffReason(e.target.value)}
              placeholder="Plans changed — I would like to be in on Thursday."
              rows={3}
            />
          </FormField>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCallingOff(null)} disabled={callOffBusy}>
              Keep my leave
            </Button>
            <Button disabled={!callOffReason.trim() || callOffBusy} onClick={() => void askToCallOff()}>
              {callOffBusy ? 'Sending…' : 'Send to my Head'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

export default function LeaveRoute() {
  return (
    <AuthGuard>
      <LeavePage />
    </AuthGuard>
  );
}
